import { useState } from 'preact/hooks'
import { Seite } from '../app.jsx'
import { ApiFehler, anmeldenWennNoetig } from '../lib/api.js'
import { zahl } from '../lib/format.js'

/**
 * Export und Import.
 *
 * Der Import läuft immer zweistufig: erst der Trockenlauf mit Bericht, dann
 * die Übernahme. Zwischen beiden steht ein Klick, damit niemand versehentlich
 * 360 Karten überschreibt.
 */

/** Eigener Aufruf statt api.js: hier geht eine Datei raus, kein JSON. */
async function sendeDatei(datei, { dryRun, overwrite, ordner }) {
  const formular = new FormData()
  formular.append('datei', datei)

  const params = new URLSearchParams({
    dry_run: dryRun ? '1' : '0',
    overwrite: overwrite ? '1' : '0',
  })
  if (ordner) params.set('folder', ordner)

  let antwort
  try {
    antwort = await fetch(`/api/import?${params}`, { method: 'POST', body: formular })
  } catch {
    throw new ApiFehler('Keine Verbindung zum Server', 0)
  }

  anmeldenWennNoetig(antwort)
  const rumpf = await antwort.json().catch(() => null)
  if (!antwort.ok) {
    throw new ApiFehler(rumpf?.message || 'Der Import wurde abgelehnt', antwort.status)
  }
  return rumpf
}

function Bericht({ bericht }) {
  return (
    <div class="bericht">
      <p class="bericht__zeile">
        <span class="zahl">{zahl(bericht.neu)}</span> neue Karten ·{' '}
        <span class="zahl">{zahl(bericht.ueberschrieben)}</span> überschrieben ·{' '}
        <span class="zahl">{zahl(bericht.unveraendert)}</span> unverändert
      </p>

      {bericht.betroffen?.length ? (
        <details class="bericht__block">
          <summary class="meta">Wird geschrieben ({bericht.betroffen.length})</summary>
          <ul class="bericht__liste meta">
            {bericht.betroffen.map((id) => (
              <li key={id}>{id}</li>
            ))}
          </ul>
        </details>
      ) : null}

      {bericht.kollisionen?.length ? (
        <details class="bericht__block">
          <summary class="meta">Gibt es schon ({bericht.kollisionen.length})</summary>
          <ul class="bericht__liste meta">
            {bericht.kollisionen.map((id) => (
              <li key={id}>{id}</li>
            ))}
          </ul>
        </details>
      ) : null}

      {bericht.uebersprungen?.length ? (
        <details class="bericht__block">
          <summary class="meta">Übersprungen ({bericht.uebersprungen.length})</summary>
          <ul class="bericht__liste meta">
            {bericht.uebersprungen.map((u) => (
              <li key={u.name}>
                {u.name} — {u.grund}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  )
}

export function Daten() {
  const [datei, setDatei] = useState(null)
  const [ordner, setOrdner] = useState('')
  const [overwrite, setOverwrite] = useState(false)
  const [bericht, setBericht] = useState(null)
  const [fertig, setFertig] = useState(false)
  const [meldung, setMeldung] = useState(null)
  const [laeuft, setLaeuft] = useState(false)

  function fehlerFangen(f) {
    setMeldung(f.message)
    setLaeuft(false)
  }

  function pruefen() {
    if (!datei) return
    setLaeuft(true)
    setMeldung(null)
    setFertig(false)
    sendeDatei(datei, { dryRun: true, overwrite, ordner })
      .then((b) => {
        setBericht(b)
        setLaeuft(false)
      })
      .catch(fehlerFangen)
  }

  function uebernehmen() {
    if (!datei || !bericht) return
    setLaeuft(true)
    setMeldung(null)
    sendeDatei(datei, { dryRun: false, overwrite, ordner })
      .then((b) => {
        setBericht(b)
        setFertig(true)
        setLaeuft(false)
      })
      .catch(fehlerFangen)
  }

  return (
    <Seite titel="Daten">
      <section class="einstellungen__block">
        <h2 class="einstellungen__titel">Export</h2>
        <p class="meta">
          Ein ZIP mit allen Kartendateien und einer <code class="md-code">zustand.json</code>, die
          Lernstände, Historie, Einstellungen und XP enthält.
        </p>
        <a class="knopf knopf--haupt" href="/api/export">
          Export herunterladen
        </a>
      </section>

      <section class="einstellungen__block">
        <h2 class="einstellungen__titel">Import</h2>
        <p class="meta">
          Entweder ein ZIP mit Markdown-Karten (auch ein eigener Export) oder eine Anki-CSV mit
          Vorderseite und Rückseite in den ersten beiden Spalten. Es wird immer zuerst geprüft.
        </p>

        <label class="feld">
          <span class="feld__label meta">Datei</span>
          <input
            class="feld__eingabe"
            type="file"
            accept=".zip,.csv,.txt"
            onChange={(e) => {
              setDatei(e.currentTarget.files?.[0] ?? null)
              setBericht(null)
              setFertig(false)
            }}
          />
        </label>

        <label class="feld">
          <span class="feld__label meta">Zielthema (optional)</span>
          <input
            class="feld__eingabe"
            value={ordner}
            placeholder="leer lassen = Struktur aus der Datei übernehmen"
            onInput={(e) => setOrdner(e.currentTarget.value)}
          />
        </label>

        <label class="einstellungen__schema">
          <input
            type="checkbox"
            checked={overwrite}
            onChange={(e) => {
              setOverwrite(e.currentTarget.checked)
              setFertig(false)
            }}
          />
          Vorhandene Karten überschreiben
        </label>

        {meldung ? (
          <p class="zustand zustand--fehler" role="alert">
            {meldung}
          </p>
        ) : null}

        {bericht ? <Bericht bericht={bericht} /> : null}

        {fertig ? (
          <p class="zustand" role="status">
            Import abgeschlossen.
          </p>
        ) : null}

        <div class="editor__aktionen">
          <button
            type="button"
            class="knopf knopf--still"
            disabled={!datei || laeuft}
            onClick={pruefen}
          >
            Prüfen
          </button>
          <button
            type="button"
            class="knopf knopf--haupt"
            disabled={!bericht || laeuft || fertig}
            onClick={uebernehmen}
          >
            Übernehmen
          </button>
        </div>
      </section>
    </Seite>
  )
}
